import React from 'react';
import i18n from '../../i18n';
import EditorForm from './EditorForm';
import FormField from './FormField';
import {
    Button,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Stack,
    Typography,
    AlertTitle,
    Alert
} from '@mui/material';
import { adaptedName } from '../../utils/dataHelpers';
import { AppDataContext } from '../../context/Context';
import { DialogSaveAdapter } from '../../adapter/DialogSaveAdapter';
import { fieldItems, isFieldVisible, isOptionWithdrawn } from '../../utils/fields';
import {
    getVisibleFieldSet,
    isFieldInVisibleSet
} from '../../utils/isFieldVisible';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import TemplateButton from '../../components/templates/TemplateButton';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import FormDialog from '../../components/dialogs/FormDialog';
import { TemplateService } from '../../services/templatesServise/TemplateService';

export default class DialogAbstract extends React.Component {
    onClose = () => {};
    onSave = (data) => {};

    Name;
    Head;
    Server;
    unsubscribe = () => {};

    constructor(props) {
        super(props);
        this.state = {
            dialogOpen: props.dialogOpen || false,
            dialogData: props.dialogData || {},
            loading: false,
            templateLockedFields: new Set(),
            confirmDiscardOpen: false,
            permissions: {} // Initialize permissions in state
        };

        this.Name = props.Name;
        this.Head = props.Head;
        this.Server = props.Server;

        this.onClose = props.onClose;
        this.onSave = props.onSave;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.dialogOpen !== state.dialogOpen) {
            return {
                dialogOpen: props.dialogOpen,
                dialogData: props.dialogData,
                loading: false
            };
        }
        return null;
    }
    static contextType = AppDataContext;

    componentDidMount() {
        this.setState({ permissions: this.context.getState().permissions });
        this.unsubscribe = this.context.subscribe(() => {
            this.setState({ permissions: this.context.getState().permissions });
        });
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    hasPermissions = () => {
        return !!Object.keys(this.state.permissions).length;
    };

    allowedPermission = (action, controller = this.Name) => {
        const adaptedController = adaptedName(controller);
        const { permissions } = this.state;

        const permission = this.hasPermissions()
            ? permissions?.[adaptedController]?.[action]
            : '';

        if (permission === 'allow_all') return true;
        if (permission === 'deny') return false;
    };

    /**
     * Receives values from react-hook-form, which only calls this once the form
     * validates — so an invalid field can no longer submit a stale value.
     *
     * Subclasses override this and are handed the values; they must not read
     * this.state.dialogData, which is now only the seed for defaultValues.
     */
    handleDialogSave = async (values) => {
        const adapter = new DialogSaveAdapter(values);
        const adaptedData = adapter.newData;
        const mode = adaptedData.id ? 'edit' : 'add';
        this.setState({ loading: true });
        try {
            await this.onSave(adaptedData, mode);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleDialogCancel = (event, name) => {
        this.handleDialogClose();
    };

    /**
     * Answers the question the old comment here asked and left open: what to do
     * if data has changed and the dialog is closing.
     *
     * Escape, the backdrop and Cancel all arrive here. With edits in the form,
     * confirm before discarding them; without, close immediately so the common
     * case stays a single keystroke.
     */
    handleDialogClose = (event, name) => {
        if (this.state.loading) return;

        if (this.form?.formState?.isDirty) {
            this.setState({ confirmDiscardOpen: true });
            return;
        }

        this.discardAndClose();
    };

    discardAndClose = () => {
        this.setState({ confirmDiscardOpen: false, dialogData: {} });
        this.onClose();
    };

    /**
     * Clears fields that a `showIfDialog` condition has just hidden, so a value
     * the user can no longer see is not submitted — and, likewise, a value an
     * `itemsFor` field, or a Select's `optionsFilter`, no longer offers.
     */
    pruneHiddenFields = (form, changedId, value) => {
        const next = { ...form.getValues(), [changedId]: value };
        const controllersData = this.context?.getState().controllersData || {};

        this.Head.forEach((field) => {
            const current = next[field.id];
            if (current == null) return;
            const hidden =
                typeof field.showIfDialog === 'function' && !field.showIfDialog(next);
            const withdrawn =
                (typeof field.itemsFor === 'function' &&
                    current !== '' &&
                    !fieldItems(field, next).includes(current)) ||
                isOptionWithdrawn(field, controllersData[field.from], current, next);
            if (hidden || withdrawn) {
                form.setValue(field.id, null, { shouldDirty: true });
            }
        });
    };


    /**
     * Consecutive fields sharing a `group` become one block. Head order is
     * preserved, so a form that declares no groups yields one block per field and
     * renders exactly as before.
     */
    groupFields = (fields) => {
        const blocks = [];

        fields.forEach((field) => {
            const group = field.group || null;
            const last = blocks[blocks.length - 1];

            if (group && last && last.group === group) {
                last.fields.push(field);
            } else {
                blocks.push({ group, fields: [field] });
            }
        });

        return blocks;
    };

    /**
     * Split blocks across two columns, balanced by field count.
     *
     * The previous version sliced the flat field list at its midpoint, which
     * could put half of a group of amounts in one column and half in the other.
     * Blocks move whole.
     */
    splitBlocks = (blocks) => {
        const total = blocks.reduce((sum, b) => sum + b.fields.length, 0);
        const target = Math.ceil(total / 2);

        const first = [];
        const second = [];
        let placed = 0;

        blocks.forEach((block) => {
            if (placed < target) {
                first.push(block);
                placed += block.fields.length;
            } else {
                second.push(block);
            }
        });

        return [first, second];
    };

    getPermissionFilterFields = (mode) => {
        let editorFields;
        if (mode === 'add') {
            editorFields = this.Head.filter(
                (f) => !f.disableEditor || (f.disableEditor && f.displayAdd)
            ).filter((f) => !f.hideOnAdd);
        } else {
            editorFields = this.Head.filter(
                (f) => !f.disableEditor || f.displayOnEditForReadOnly
            );
        }
        return editorFields.filter((f) =>
            f.from ? this.allowedPermission('read', f.from) : true
        );
    };


    renderField = (field, form, values, mode, autoFocus) => {
        const isLocked = Boolean(
            this.state.templateLockedFields &&
                this.state.templateLockedFields.has(field.id)
        );

        return (
            <FormField
                key={field.id}
                field={field}
                control={form.control}
                values={values}
                mode={mode}
                context={this.context}
                locked={isLocked}
                autoFocus={autoFocus}
                onAfterChange={(id, value) =>
                    this.pruneHiddenFields(form, id, value)
                }
            />
        );
    };

    /**
     * @param autoFocusId id of the one field that should take focus. Exactly one
     * does: three components used to request autoFocus themselves, so which one
     * won depended on mount order.
     */
    renderBlocks = (blocks, form, values, mode, autoFocusId) =>
        blocks.map((block) => (
            <Stack spacing={2} key={block.group || block.fields[0].id}>
                {block.group && (
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5 }}
                    >
                        {i18n.t(`fieldGroups.${block.group}`)}
                    </Typography>
                )}
                {block.fields.map((field) =>
                    this.renderField(
                        field,
                        form,
                        values,
                        mode,
                        field.id === autoFocusId
                    )
                )}
            </Stack>
        ));

    computeLockedFieldsFromTemplate = (tplData) => {
        const locks = new Set();
        (this.Head || []).forEach((f) => {
            if (!f || !f.id) return;

            const shouldLock =
                typeof f.lockOnTemplate === 'boolean' ? f.lockOnTemplate : true;

            if (!shouldLock) return;

            if (Object.prototype.hasOwnProperty.call(tplData, f.id)) {
                const v = tplData[f.id];
                if (v !== undefined) {
                    locks.add(f.id);
                }
            }
        });
        return locks;
    };


    /**
     * Cells edited as a checkbox — see the Fields registry in ../editors.
     * A new row has to start them at false rather than undefined.
     */
    static BOOLEAN_CELLS = ['Boolean', 'Checkbox', 'EditableBoolean'];

    /**
     * Seed for the form.
     *
     * An unchecked checkbox and an absent value look identical on screen, so a
     * new record used to submit no isArchived at all and the API answered
     * "Archived: must not be null". Booleans therefore start explicitly false on
     * add; edit keeps whatever the record holds.
     *
     * Seeded from the whole Head rather than the fields actually rendered: a
     * boolean hidden by role visibility still has to be sent.
     */
    buildDefaultValues(dialogData, mode) {
        if (mode !== 'add') return dialogData;

        const seeded = { ...dialogData };
        (this.Head || []).forEach((field) => {
            // `== null` catches null as well as undefined. It used to test
            // `=== undefined`, which never matched: GridDataAdapter.transform
            // builds the add payload with every field present and explicitly
            // null, so the seed never applied and the form posted
            // isArchived: null.
            if (
                DialogAbstract.BOOLEAN_CELLS.includes(field.cell) &&
                seeded[field.id] == null
            ) {
                seeded[field.id] = false;
            }
        });
        return seeded;
    }

    render() {
        const { dialogData, dialogOpen, loading } = this.state;
        const { role, error } = this.props;

        if (!this.hasPermissions() || !dialogData) {
            return null;
        }

        const mode = dialogData?.id ? 'edit' : 'add';
        const editMode = mode === 'edit';

        const visibleFieldSet = getVisibleFieldSet(role);
        const availableFields = this.getPermissionFilterFields(mode).filter(
            (field) => isFieldInVisibleSet(field, visibleFieldSet, role)
        );

        // Width is decided before `showIfDialog` narrows the list, so a dialog
        // does not resize as conditional fields appear and disappear.
        //
        // The threshold was >9, which only one form of thirty ever reached, so
        // seven- to nine-field forms scrolled in a narrow column while half the
        // dialog went unused.
        const twoColumn = availableFields.length > 6;

        return (
            <>
                <FormDialog
                    open={dialogOpen}
                    onClose={this.handleDialogClose}
                    fullWidth
                    maxWidth={twoColumn ? 'md' : 'sm'}
                >
                    <EditorForm
                        defaultValues={this.buildDefaultValues(dialogData, mode)}
                        resetKey={dialogData?.id ?? 'new'}
                        onSubmit={this.handleDialogSave}
                    >
                        {(form) => {
                            // The form API lives inside the render prop, but
                            // handleDialogClose runs outside it. Held on the instance
                            // rather than in state: it is read in handlers, never
                            // rendered from, so it must not trigger a re-render.
                            this.form = form;

                            // Whole-form values, so conditional visibility and the
                            // `data` prop both see the live state of the form.
                            const values = form.watch();
                            const fields = availableFields.filter((field) =>
                                isFieldVisible(field, values, 'dialog')
                            );
                            const autoFocusId = fields[0]?.id;
                            const blocks = this.groupFields(fields);
                            // A `fullRow` field — an editor that needs the room,
                            // such as a gateway's settings — goes under both
                            // columns rather than into one of them.
                            const [firstBlocks, secondBlocks] = this.splitBlocks(
                                this.groupFields(fields.filter((field) => !field.fullRow))
                            );
                            const fullRowBlocks = this.groupFields(
                                fields.filter((field) => field.fullRow)
                            );

                            return (
                                <>
                                    <DialogTitle>
                                        {editMode
                                            ? i18n.t(`pages.${this.Name}.edit`)
                                            : i18n.t(`pages.${this.Name}.add`)}
                                    </DialogTitle>
                                    <DialogContent dividers>
                                        {error ? (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                <AlertTitle>
                                                    {i18n.t('errors.title')}
                                                </AlertTitle>
                                                {/* Not a <ul>: global styles zero out
                                                    list padding, so the markers were
                                                    clipped and the lines sat flush
                                                    against the icon. */}
                                                <Stack component="span" spacing={0.5}>
                                                    {error.map((err, index) => (
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            key={index}
                                                        >
                                                            {err}
                                                        </Typography>
                                                    ))}
                                                </Stack>
                                            </Alert>
                                        ) : null}

                                        {/* One spacing rhythm, owned by the
                                            container. The fields used to space
                                            themselves, half with margin="dense" and
                                            half with a FormControl top margin. */}
                                        {!twoColumn ? (
                                            <Stack spacing={2.5}>
                                                {this.renderBlocks(
                                                    blocks,
                                                    form,
                                                    values,
                                                    mode,
                                                    autoFocusId
                                                )}
                                            </Stack>
                                        ) : (
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <Stack spacing={2.5}>
                                                        {this.renderBlocks(
                                                            firstBlocks,
                                                            form,
                                                            values,
                                                            mode,
                                                            autoFocusId
                                                        )}
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Stack spacing={2.5}>
                                                        {this.renderBlocks(
                                                            secondBlocks,
                                                            form,
                                                            values,
                                                            mode,
                                                            autoFocusId
                                                        )}
                                                    </Stack>
                                                </Grid>
                                                {fullRowBlocks.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <Stack spacing={2.5}>
                                                            {this.renderBlocks(
                                                                fullRowBlocks,
                                                                form,
                                                                values,
                                                                mode,
                                                                autoFocusId
                                                            )}
                                                        </Stack>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        {this.props.showTemplateButton &&
                                            mode === 'add' && (
                                            <TemplateButton
                                                label={i18n.t(
                                                    'buttons.fillFromTemplate'
                                                )}
                                                loadTemplates={
                                                    this.props.loadTemplates
                                                }
                                                fieldMapping={
                                                    this.props.templateMapping
                                                }
                                                disabled={loading}
                                                onApply={(tpl) => {
                                                    // reset(), not setState: the form
                                                    // owns the values now, and this
                                                    // is what makes a template reach
                                                    // every field.
                                                    const merged =
                                                            TemplateService.applyTemplate(
                                                                form.getValues(),
                                                                tpl.data,
                                                                this.Head
                                                            );
                                                    form.reset(merged);
                                                    this.setState({
                                                        templateLockedFields:
                                                                this.computeLockedFieldsFromTemplate(
                                                                    tpl.data
                                                                )
                                                    });
                                                }}
                                            />
                                        )}
                                    </DialogContent>
                                    <DialogActions sx={{ px: 3, py: 2 }}>
                                        <Button
                                            onClick={this.handleDialogCancel}
                                            disabled={loading}
                                            color="inherit"
                                        >
                                            {i18n.t('buttons.cancel')}
                                        </Button>
                                        {/* type=submit so Enter and the click take
                                            the same validated path, and disabled
                                            while the form does not validate — an
                                            invalid amount used to be submittable,
                                            carrying the previous value with it. */}
                                        <LoadingButton
                                            type="submit"
                                            variant="contained"
                                            loading={loading}
                                            disabled={!form.formState.isValid}
                                        >
                                            {i18n.t('buttons.save')}
                                        </LoadingButton>
                                    </DialogActions>
                                </>
                            );
                        }}
                    </EditorForm>
                </FormDialog>

                <ConfirmDialog
                    dialogOpen={this.state.confirmDiscardOpen}
                    alertTitle={i18n.t('dialogs.confirmationTitle')}
                    alertText={i18n.t('dialogs.discardChanges')}
                    onConfirm={this.discardAndClose}
                    onCancel={() => this.setState({ confirmDiscardOpen: false })}
                />
            </>
        );
    }
}
DialogAbstract.propTypes = {
    dialogOpen: PropTypes.bool.isRequired,
    dialogData: PropTypes.object || null,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    Name: PropTypes.string,
    Head: PropTypes.arrayOf(PropTypes.object).isRequired,
    Server: PropTypes.object,
    role: PropTypes.string.isRequired
};
